<script setup lang="ts">
import {
  type Component,
  computed,
  inject,
  onMounted,
  onUnmounted,
  ref,
  useTemplateRef,
  type VNode,
} from "vue";
import type { Placement } from "../../models/types";
import { MenuEntry } from "../../models/menu-entry";
import BcButton from "../../components/bc-button.vue";
import BcTooltip from "../../components/bc-tooltip.vue";
import BcMenu from "../../components/menu/bc-menu.vue";
import {
  CONTROLS_DESIGN_VERSION,
  DEFAULT_CONTROLS_DESIGN_VERSION,
  DEFAULT_DESIGN_VERSION,
  DESIGN_VERSION,
} from "../../models";

export type BcPopupMenuProps = {
  /** Closes menu on item select */
  closeOnSelect?: boolean;
  /** Specifies the timeout to wait before close the popup */
  closeTimeout?: number;
  /** Disabled the popup control */
  disabled?: boolean;
  disableHeightCalculation?: boolean;
  /** Sets a divider between items */
  separateAll?: boolean;
  /** Specifies the selectable items */
  items?: MenuEntry[];
  /** Specifies the placement of the menu items compared to the container element */
  placement?: Placement;
  subItemIcon?: VNode | Component;
  /** Specifies the title for the popup */
  title?: string;
  textWrap?: boolean;
  offset?: number;
  teleportTarget?: string;
};

export type BcPopupMenuEmits = {
  (event: "trigger"): void;
  (event: "select", payload: MenuEntry): void;
};

const {
  closeOnSelect = true,
  closeTimeout = 300,
  disabled = false,
  disableHeightCalculation = false,
  separateAll = true,
  items,
  placement = "bottom-start",
  subItemIcon,
  title,
  textWrap = false,
  offset = 3,
  teleportTarget,
} = defineProps<BcPopupMenuProps>();

const design = inject(
  DESIGN_VERSION,
  computed(() => DEFAULT_DESIGN_VERSION),
);
const controlsDesign = inject(
  CONTROLS_DESIGN_VERSION,
  computed(() => DEFAULT_CONTROLS_DESIGN_VERSION),
);
const pak = computed(() => `bc-controls-d${controlsDesign.value}`);

const emit = defineEmits<BcPopupMenuEmits>();

const showPopup = ref(false);
/** Popup menu reference */
const popupMenuRef = useTemplateRef("popupMenuRef");
/** Popup trigger reference */
const popupTriggerRef = useTemplateRef("popupTriggerRef");
/** Menu reference */
const menuRef = useTemplateRef("menuRef");

/** Specifies the evaluated expression to prevent (if needed) */
let hideLastCall: number | undefined = undefined;

function clearTimeoutMethod(): void {
  window.clearTimeout(hideLastCall);
}

/** Handling button's `onclick` event */
function triggerPopup(): void {
  if (disabled) return;

  emit("trigger");
  showPopup.value = !showPopup.value;
}

/**
 * Notifying parent component if an item is clicked.
 * Item's value will be emitted to the parent.
 * @param {ListItem} item clicked item
 */
function selectItem(item: MenuEntry): void {
  if (disabled || item.disabled) return;
  /** When popup list item is selected this event is fired. */
  emit("select", item);
  if (closeOnSelect) {
    clearTimeoutMethod();
    showPopup.value = false;
  }
}

/** Hides popup */
function hide(): void {
  clearTimeoutMethod();

  hideLastCall = window.setTimeout(
    () => {
      showPopup.value = false;
    },
    Math.max(100, closeTimeout),
  );
}

function hideOnClose(event: MouseEvent): void {
  if (!showPopup.value) return;

  const target = event.target as Node;
  if (
    menuRef.value &&
    popupMenuRef.value &&
    !(menuRef.value.$el as HTMLElement).contains(target) &&
    !popupMenuRef.value.contains(target)
  ) {
    clearTimeoutMethod();
    showPopup.value = false;
  }
}

onMounted(() => {
  document.addEventListener("click", hideOnClose);
});

onUnmounted(() => {
  document.removeEventListener("click", hideOnClose);
});

defineExpose({
  popupTriggerRef,
  showPopup,
  triggerPopup,
  selectItem,
  hide,
});
</script>

<template>
  <div
    v-bind:data-pak="pak"
    v-bind:data-design="design"
    class="bissantzControls popupMenuControl"
    v-bind:class="{ disabled: disabled }"
    ref="popupMenuRef"
    v-on:mouseenter="clearTimeoutMethod"
    v-on:mouseleave="hide"
  >
    <BcTooltip
      v-bind:mouseMode="controlsDesign !== '6'"
      v-bind:contentDesign="controlsDesign === '6' ? 'dark' : 'mouseMode'"
      v-bind:content="disabled || controlsDesign === '6' ? '' : title"
    >
      <div
        ref="popupTriggerRef"
        class="popupTrigger"
        v-bind:class="{
          defaultTrigger: !$slots['popup-trigger'],
        }"
        v-bind:title="controlsDesign === '6' ? title : undefined"
        v-on:click="triggerPopup"
      >
        <slot name="popup-trigger">
          <BcButton />
        </slot>
      </div>
    </BcTooltip>

    <BcMenu
      ref="menuRef"
      class="bc-popup-menu"
      v-bind:disableHeightCalculation="disableHeightCalculation"
      v-bind:items="items"
      v-bind:isVisible="showPopup"
      v-bind:parentRect="popupTriggerRef"
      v-bind:placement="placement"
      v-bind:separateAll="separateAll"
      v-bind:subItemIcon="subItemIcon"
      v-bind:textWrap="textWrap"
      v-bind:offset="offset"
      v-bind:teleportTarget="teleportTarget"
      v-on:click="selectItem($event)"
      v-on:mouseenter="clearTimeoutMethod"
      v-on:mouseleave="hide"
    >
      <slot name="menu-items"></slot>
    </BcMenu>
  </div>
</template>

<style lang="less" scoped>
.bissantzControls.popupMenuControl {
  display: inline-block;
  position: relative;
  width: fit-content;
  height: fit-content;

  .popupTrigger {
    cursor: pointer;
  }

  .popupTrigger:not(.defaultTrigger) {
    stroke: var(--popup-trigger-icon-color);
    fill: var(--popup-trigger-icon-color);
  }

  .popupTrigger:not(.defaultTrigger):hover {
    color: var(--input-primary);
    stroke: var(--input-primary);
    fill: var(--input-primary);
  }

  .popupTrigger.defaultTrigger button {
    min-width: 20px;
    min-height: 20px;
  }

  &.disabled .popupTrigger:not(.defaultTrigger) {
    color: var(--input-disabled-color);
    stroke: var(--input-disabled-color);
    fill: var(--input-disabled-color);
  }

  .bissantzControls.buttonControl {
    border: none;
  }
}
</style>
